import { useState } from "react";
import { useSelector } from "react-redux";
import {
  getWheelRaffleErrorMessage,
  useGetRaffleMetaQuery,
} from "entities/wheel-raffle";
import type { CreateWheelRaffleResponse } from "entities/wheel-raffle";
import type { RootState } from "store";
import { useCreateWheelRaffleMutation } from "../api/createWheelRaffleApi";
import { DEFAULT_DURATION_LIMITS } from "./duration";
import { EMPTY_VALUES, toCreateRequest } from "./formValues";
import type { RaffleFormValues } from "./formValues";
import { hasRaffleErrors, validateRaffle } from "./validateRaffle";
import type { RaffleErrors } from "./validateRaffle";

const NO_ERRORS: RaffleErrors = {};

export const useCreateRaffle = (
  onCreated?: (raffle: CreateWheelRaffleResponse) => void,
) => {
  const realBalance = useSelector(
    (state: RootState) => state.trading.balance.real,
  );
  const meta = useGetRaffleMetaQuery();
  const limits = meta.data?.duration_limits ?? DEFAULT_DURATION_LIMITS;
  const [values, setValues] = useState(EMPTY_VALUES);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [createRaffle, { isLoading, error, reset }] =
    useCreateWheelRaffleMutation();

  const errors = validateRaffle(values, realBalance, limits);

  const setValue = <Field extends keyof RaffleFormValues>(
    field: Field,
    value: RaffleFormValues[Field],
  ) => {
    setValues((prev) => ({ ...prev, [field]: value }));
    reset();
  };

  const submit = async () => {
    setIsSubmitted(true);
    if (hasRaffleErrors(errors) || !meta.data) return;
    const request = toCreateRequest(values, meta.data.participant_tiers);
    const result = await createRaffle(request);
    if (result.data) onCreated?.(result.data);
  };

  return {
    values,
    setValue,
    realBalance,
    meta: meta.data,
    limits,
    isMetaLoading: meta.isLoading,
    isMetaError: meta.isError,
    // Ошибки полей показываются только после первой попытки отправки.
    errors: isSubmitted ? errors : NO_ERRORS,
    serverError: getWheelRaffleErrorMessage(error),
    submit,
    isSubmitting: isLoading,
  };
};

export type CreateRaffleForm = ReturnType<typeof useCreateRaffle>;
